import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, convertFromMXN, convertToMXN } from '../../utils/formatters';
import { DollarSign, Target, Calendar, Percent, ChevronRight, Plus, Minus, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { getLanguageContent, getLanguageFromCurrency } from '../../data/languages';

interface TraditionalCentralDataProps {
  onNext: () => void;
}

const TraditionalCentralData: React.FC<TraditionalCentralDataProps> = ({ onNext }) => {
  const { investment, updateInvestment } = useCalculator();
  
  // Get current language content
  const currentLanguage = investment.language || getLanguageFromCurrency(investment.currencyFormat);
  const content = getLanguageContent(currentLanguage);

  // Handlers for increment/decrement buttons
  const handleIncrementCertificates = () => {
    if (investment.initialCertificates < 20) {
      updateInvestment({ initialCertificates: investment.initialCertificates + 1 });
    }
  };

  const handleDecrementCertificates = () => {
    if (investment.initialCertificates > 1) {
      updateInvestment({ initialCertificates: investment.initialCertificates - 1 });
    }
  };

  const handleIncrementPrice = () => {
    const displayPrice = convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const increment = convertToMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const maxPrice = convertToMXN(300000, 'MXN', investment.exchangeRate, investment.exchangeRateEUR);
    
    if (investment.certificateBasePrice < maxPrice) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice + increment });
    }
  };

  const handleDecrementPrice = () => {
    const displayPrice = convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const decrement = convertToMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
    const minPrice = convertToMXN(200000, 'MXN', investment.exchangeRate, investment.exchangeRateEUR);
    
    if (investment.certificateBasePrice > minPrice) {
      updateInvestment({ certificateBasePrice: investment.certificateBasePrice - decrement });
    }
  };

  const handleIncrementYears = () => {
    if (investment.years < 30) {
      updateInvestment({ years: investment.years + 1 });
    }
  };

  const handleDecrementYears = () => {
    if (investment.years > 5) {
      updateInvestment({ years: investment.years - 1 });
    }
  };

  const handleIncrementProfit = () => {
    if (investment.annualProfit < 30) {
      updateInvestment({ annualProfit: investment.annualProfit + 1 });
    }
  };

  const handleDecrementProfit = () => {
    if (investment.annualProfit > 10) {
      updateInvestment({ annualProfit: investment.annualProfit - 1 });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-4xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Target className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{content.traditional.centralData.title}</h2>
              <p className="text-green-100">{content.traditional.centralData.subtitle}</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Initial Certificates */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{content.traditional.centralData.certificates.title}</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementCertificates}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.initialCertificates <= 1}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="1"
                  max="20"
                  value={investment.initialCertificates}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 1 && value <= 20) {
                      updateInvestment({ initialCertificates: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  title={currentLanguage === 'es' ? 
                    "Número de certificados iniciales a adquirir. Cada certificado representa 1,000 m² de plantación de limón persa. Rango: 1-20 certificados" :
                    currentLanguage === 'en' ?
                    "Number of initial certificates to acquire. Each certificate represents 1,000 m² of Persian lime plantation. Range: 1-20 certificates" :
                    "Nombre de certificats initiaux à acquérir. Chaque certificat représente 1 000 m² de plantation de citron vert persan. Plage: 1-20 certificats"}
                />
                <div className="text-sm text-green-600">
                  {content.traditional.centralData.certificates.label}
                </div>
              </div>
              <button 
                onClick={handleIncrementCertificates}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.initialCertificates >= 20}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="1"
                max="20"
                step="1"
                value={investment.initialCertificates}
                onChange={(e) => updateInvestment({ initialCertificates: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
                title={currentLanguage === 'es' ? 
                  "Desliza para ajustar el número de certificados. Cada certificado equivale a 1,000 m² de plantación productiva de limón persa con potencial de generar ingresos pasivos" :
                  currentLanguage === 'en' ?
                  "Slide to adjust the number of certificates. Each certificate equals 1,000 m² of productive Persian lime plantation with potential to generate passive income" :
                  "Glissez pour ajuster le nombre de certificats. Chaque certificat équivaut à 1 000 m² de plantation productive de citron vert persan avec potentiel de générer des revenus passifs"}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>1</span>
                <span>20</span>
              </div>
            </div>

            <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
              <div className="text-xs text-gray-600 mb-1">
                {currentLanguage === 'es' ? 'Inversión inicial:' :
                 currentLanguage === 'en' ? 'Initial investment:' :
                 'Investissement initial:'}
              </div>
              <div className="text-xl font-bold text-gray-900">
                {formatCurrency(convertFromMXN(investment.initialCertificates * investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}
              </div>
            </div>
          </div>

          {/* Certificate Price */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{content.traditional.centralData.price.title}</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementPrice}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.certificateBasePrice <= 200000}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min={convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                  max={convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                  step={convertFromMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                  value={Math.round(convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR))}
                  onChange={(e) => {
                    const displayValue = parseInt(e.target.value);
                    if (!isNaN(displayValue)) {
                      const mxnValue = convertToMXN(displayValue, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
                      const minPrice = 200000;
                      const maxPrice = 300000;
                      if (mxnValue >= minPrice && mxnValue <= maxPrice) {
                        updateInvestment({ certificateBasePrice: mxnValue });
                      }
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-32 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  title={currentLanguage === 'es' ? 
                    "Precio base del certificado antes de apreciación. Este valor se aprecia 12% anual durante los primeros 5 años. Rango: 200K-300K" :
                    currentLanguage === 'en' ?
                    "Base certificate price before appreciation. This value appreciates 12% annually during the first 5 years. Range: 200K-300K" :
                    "Prix de base du certificat avant appréciation. Cette valeur s'apprécie de 12% par an pendant les 5 premières années. Plage: 200K-300K"}
                />
                <div className="text-sm text-blue-600">
                  {content.traditional.centralData.price.label}
                </div>
              </div>
              <button 
                onClick={handleIncrementPrice}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.certificateBasePrice >= 300000}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min={convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                max={convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                step={convertFromMXN(500, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                value={convertFromMXN(investment.certificateBasePrice, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR)}
                onChange={(e) => {
                  const displayValue = parseFloat(e.target.value);
                  const mxnValue = convertToMXN(displayValue, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR);
                  updateInvestment({ certificateBasePrice: mxnValue });
                }}
                className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                title={currentLanguage === 'es' ? 
                  "Desliza para ajustar el precio base. El precio se aprecia automáticamente durante los primeros 5 años, luego puede crecer con el precio del limón" :
                  currentLanguage === 'en' ?
                  "Slide to adjust the base price. The price appreciates automatically during the first 5 years, then can grow with lemon price" :
                  "Glissez pour ajuster le prix de base. Le prix s'apprécie automatiquement pendant les 5 premières années, puis peut croître avec le prix du citron"}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>{formatCurrency(convertFromMXN(200000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</span>
                <span>{formatCurrency(convertFromMXN(300000, investment.currencyFormat, investment.exchangeRate, investment.exchangeRateEUR), investment.currencyFormat)}</span>
              </div>
            </div>
          </div>

          {/* Investment Horizon */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{content.traditional.centralData.horizon.title}</h3>
            </div>
            
            <div className="flex items-center justify-center gap-4 mb-5">
              <button 
                onClick={handleDecrementYears}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.years <= 5}
              >
                <Minus className="w-5 h-5 text-gray-700" />
              </button>
              <div className="text-center">
                <input
                  type="number"
                  min="5"
                  max="30"
                  value={investment.years}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 5 && value <= 30) {
                      updateInvestment({ years: value });
                    }
                  }}
                  onWheel={(e) => e.currentTarget.blur()}
                  className="w-20 text-center text-3xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                  title={currentLanguage === 'es' ? 
                    "Horizonte de inversión en años. Determina el período total de análisis. Los primeros 4 años son de maduración, la producción inicia en el año 5" :
                    currentLanguage === 'en' ?
                    "Investment horizon in years. Determines the total analysis period. The first 4 years are maturation, production starts in year 5" :
                    "Horizon d'investissement en années. Détermine la période totale d'analyse. Les 4 premières années sont de maturation, la production commence en année 5"}
                />
                <div className="text-sm text-purple-600">
                  {content.common.units.years}
                </div>
              </div>
              <button 
                onClick={handleIncrementYears}
                className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                disabled={investment.years >= 30}
              >
                <Plus className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
              {[5, 10, 15, 20, 25, 30].map((years) => (
                <button
                  key={years}
                  onClick={() => updateInvestment({ years })}
                  className={`py-3 px-3 rounded-xl text-sm font-medium transition-all duration-200 ${
                    investment.years === years
                      ? 'bg-gradient-to-br from-purple-500 to-purple-600 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={`Establecer horizonte de inversión a ${years} años`}
                >
                  {years}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <input
                type="range"
                min="5"
                max="30"
                step="1"
                value={investment.years}
                onChange={(e) => updateInvestment({ years: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-purple-200 to-purple-300 rounded-lg appearance-none cursor-pointer"
                title={currentLanguage === 'es' ? 
                  "Desliza para ajustar el horizonte temporal. Considera que los primeros 4 años son de maduración y la generación de ingresos inicia en el año 5" :
                  currentLanguage === 'en' ?
                  "Slide to adjust the time horizon. Consider that the first 4 years are maturation and income generation starts in year 5" :
                  "Glissez pour ajuster l'horizon temporel. Considérez que les 4 premières années sont de maturation et la génération de revenus commence en année 5"}
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>5 {content.common.units.years}</span>
                <span>30 {content.common.units.years}</span>
              </div>
            </div>
          </div>

          {/* Annual Profit */}
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">{content.traditional.centralData.production.title}</h3>
            </div>
            
            {/* Production per Hectare */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">{content.traditional.centralData.production.productionLabel}</label>
              <div className="flex items-center justify-center gap-4 mb-3">
                <button 
                  onClick={() => updateInvestment({ averageProductionPerHectare: Math.max(15000, investment.averageProductionPerHectare - 500) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageProductionPerHectare <= 15000}
                >
                  <Minus className="w-5 h-5 text-gray-700" />
                </button>
                <div className="text-center">
                  <input
                    type="number"
                    min="15000"
                    max="35000"
                    step="500"
                    value={investment.averageProductionPerHectare}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 15000 && value <= 35000) {
                        updateInvestment({ averageProductionPerHectare: value });
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-24 text-center text-2xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    title={currentLanguage === 'es' ? 
                      "Producción promedio anual por hectárea en kilogramos. Basado en datos históricos de plantaciones de limón persa en México. Rango: 15,000-35,000 kg/ha" :
                      currentLanguage === 'en' ?
                      "Average annual production per hectare in kilograms. Based on historical data from Persian lime plantations in Mexico. Range: 15,000-35,000 kg/ha" :
                      "Production annuelle moyenne par hectare en kilogrammes. Basé sur les données historiques des plantations de citron vert persan au Mexique. Plage: 15,000-35,000 kg/ha"}
                  />
                  <div className="text-sm text-green-600">
                    {currentLanguage === 'es' ? 'kg/hectárea' :
                     currentLanguage === 'en' ? 'kg/hectare' :
                     'kg/hectare'}
                  </div>
                </div>
                <button 
                  onClick={() => updateInvestment({ averageProductionPerHectare: Math.min(35000, investment.averageProductionPerHectare + 500) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageProductionPerHectare >= 35000}
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              
              <input
                type="range"
                min="15000"
                max="35000"
                step="500"
                value={investment.averageProductionPerHectare}
                onChange={(e) => updateInvestment({ averageProductionPerHectare: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-green-200 to-green-300 rounded-lg appearance-none cursor-pointer"
                title="Desliza para ajustar la producción. Valores conservadores: 25,566 kg/ha, moderados: 27,341 kg/ha, optimistas: 28,259 kg/ha"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>15,000 kg</span>
                <span>35,000 kg</span>
              </div>
            </div>

            {/* Price per Kg */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">{content.traditional.centralData.production.priceLabel}</label>
              <div className="flex items-center justify-center gap-4 mb-3">
                <button 
                  onClick={() => updateInvestment({ averageSalePricePerKg: Math.max(20, investment.averageSalePricePerKg - 1) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageSalePricePerKg <= 20}
                >
                  <Minus className="w-5 h-5 text-gray-700" />
                </button>
                <div className="text-center">
                  <input
                    type="number"
                    min="20"
                    max="50"
                    step="1"
                    value={investment.averageSalePricePerKg}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 20 && value <= 50) {
                        updateInvestment({ averageSalePricePerKg: value });
                      }
                    }}
                    onWheel={(e) => e.currentTarget.blur()}
                    className="w-20 text-center text-2xl font-bold text-gray-900 mb-1 border-0 focus:ring-0 focus:outline-none"
                    title={currentLanguage === 'es' ? 
                      "Precio promedio de venta por kilogramo en pesos mexicanos. Basado en precios FOB de exportación. Rango: $20-$50 MXN/kg" :
                      currentLanguage === 'en' ?
                      "Average sale price per kilogram in Mexican pesos. Based on FOB export prices. Range: $20-$50 MXN/kg" :
                      "Prix de vente moyen par kilogramme en pesos mexicains. Basé sur les prix FOB d'exportation. Plage: $20-$50 MXN/kg"}
                  />
                  <div className="text-sm text-blue-600">
                    MXN/kg
                  </div>
                </div>
                <button 
                  onClick={() => updateInvestment({ averageSalePricePerKg: Math.min(50, investment.averageSalePricePerKg + 1) })}
                  className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                  disabled={investment.averageSalePricePerKg >= 50}
                >
                  <Plus className="w-5 h-5 text-gray-700" />
                </button>
              </div>
              
              <input
                type="range"
                min="20"
                max="50"
                step="1"
                value={investment.averageSalePricePerKg}
                onChange={(e) => updateInvestment({ averageSalePricePerKg: parseInt(e.target.value) })}
                className="w-full h-2 bg-gradient-to-r from-blue-200 to-blue-300 rounded-lg appearance-none cursor-pointer"
                title="Desliza para ajustar el precio de venta. Precios conservadores: $28/kg, moderados: $32/kg, optimistas: $38/kg"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>$20 MXN</span>
                <span>$50 MXN</span>
              </div>
            </div>

            {/* Calculated Annual Profit Display */}
            <div className="mt-4 p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
              <div className="text-xs text-gray-600 mb-1">{content.traditional.centralData.production.calculatedProfit}</div>
              <div className="text-xl font-bold text-gray-900">
                {((investment.averageProductionPerHectare * investment.averageSalePricePerKg * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}%
              </div>
              <div className="text-xs text-green-600 mt-1">
                {currentLanguage === 'es' ? 'Basado en' :
                 currentLanguage === 'en' ? 'Based on' :
                 'Basé sur'} {investment.averageProductionPerHectare.toLocaleString()} kg/ha × ${investment.averageSalePricePerKg}/kg × 0.1 × 0.65
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                { label: content.traditional.centralData.production.scenarios.conservative, production: 25000, price: 30 },
                { label: content.traditional.centralData.production.scenarios.moderate, production: 30000, price: 35 },
                { label: content.traditional.centralData.production.scenarios.optimistic, production: 35000, price: 40 }
              ].map((scenario) => (
                <button
                  key={scenario.label}
                  onClick={() => updateInvestment({ 
                    averageProductionPerHectare: scenario.production,
                    averageSalePricePerKg: scenario.price
                  })}
                  className={`py-2 px-3 rounded-xl text-sm font-medium transition-colors ${
                    investment.averageProductionPerHectare === scenario.production && investment.averageSalePricePerKg === scenario.price
                      ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-md transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={`${currentLanguage === 'es' ? 'Escenario' : currentLanguage === 'en' ? 'Scenario' : 'Scénario'} ${scenario.label}: ${scenario.production.toLocaleString()} kg/ha × $${scenario.price}/kg = ${((scenario.production * scenario.price * 0.1 * 0.65 / investment.certificateBasePrice) * 100).toFixed(1)}% ${currentLanguage === 'es' ? 'utilidad anual' : currentLanguage === 'en' ? 'annual profit' : 'profit annuel'}`}
                >
                  {scenario.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Continue Button */}
          <div className="flex justify-end">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNext}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>{content.common.buttons.continue}</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TraditionalCentralData;